import {useEffect, useState} from "react";
import Roles, {roleHierarchy} from "../../shared/models/roles";
import {useGetUserRolesQuery, useIsInstructorQuery} from "../generated/graphql";

const pollIntervallInMilliseconds = undefined;

const useHighestRole = (): Roles => {
    const [highestRole, setHighestRole] = useState<Roles>(Roles.Instructor);

    // ---------------------------------------------------------------------
    const [rolesOfCurrent, setRolesOfCurrent] = useState<Array<Roles>>([
        Roles.Instructor,
    ]);
    const {data: rolesData, loading: loadingRoles} = useGetUserRolesQuery({
        pollInterval: pollIntervallInMilliseconds,
    });

    const {data: isInstructorData} = useIsInstructorQuery();
    const isInstructor = isInstructorData?.isInstructor;

    useEffect(() => {
        if (rolesData?.getUserRoles?.rolesOfCurrent && !loadingRoles) {
            setRolesOfCurrent(rolesData.getUserRoles.rolesOfCurrent);
        }
    }, [rolesData, loadingRoles]);

    const getHighestRole = (roles: Array<Roles>) => {
        if (roles.includes(Roles.Superuser)) {
            return Roles.Superuser;
        } else if (roles.includes(Roles.SuperAdmin)) {
            return Roles.SuperAdmin;
        } else if (roles.includes(Roles.Admin)) {
            return Roles.Admin;
        } else if (roles.includes(Roles.AreaManager)) {
            return Roles.AreaManager;
        } else if (roles.includes(Roles.Instructor)) {
            return Roles.Instructor;
        } else if (roles.includes(Roles.Staff)) {
            return Roles.Staff;
        }

        return Roles.EndUser;
    };

    useEffect(() => {
        setHighestRole(getHighestRole(rolesOfCurrent));
    }, [rolesOfCurrent]);

    return highestRole;
};

/**
 * Prüft ob der aktuelle User die BetaTester-Rolle hat,
 * unabhängig von der Rollen-Hierarchie.
 */
export const useIsBetaTester = (): boolean => {
    const {data, loading} = useGetUserRolesQuery({
        pollInterval: pollIntervallInMilliseconds,
    });

    if (loading || !data?.getUserRoles?.rolesOfCurrent) {
        return false;
    }

    return data.getUserRoles.rolesOfCurrent.includes(Roles.BetaTester);
};

/**
 * Prüft ob der aktuelle User die Superuser-Rolle hat, unabhängig von der
 * Rollen-Hierarchie. v2.2 Block 4: Basis für das Dev-Docs-Info-Icon im
 * MollieSettings-Header (Admins sehen das nicht).
 *
 * Boolean-Variante für UI-Visibility-Checks. Caller, die die Loading-Phase
 * unterscheiden müssen (z.B. um eine Redirect-Entscheidung NICHT verfrüht
 * zu treffen), nutzen `useSuperuserStatus()`.
 */
export const useIsSuperuser = (): boolean => {
    return useSuperuserStatus().isSuperuser;
};

/**
 * Liefert {isSuperuser, loading} für Caller, die die Race zwischen
 * "Rollen noch nicht geladen" und "definitiv kein Superuser" unterscheiden
 * müssen. v2.2 Block 4: nötig für die Redirect-Logik in
 * MollieDeveloperDocs.tsx — wir wollen NICHT redirecten, solange `loading`
 * true ist.
 */
export const useSuperuserStatus = (): {isSuperuser: boolean; loading: boolean} => {
    const {data, loading} = useGetUserRolesQuery({
        pollInterval: pollIntervallInMilliseconds,
    });

    const decided = !loading && !!data?.getUserRoles?.rolesOfCurrent;
    const isSuperuser = decided
        ? data!.getUserRoles!.rolesOfCurrent!.includes(Roles.Superuser)
        : false;

    return {isSuperuser, loading: !decided};
};

export default useHighestRole;

/**
 * true für Admin und alles darüber in `roleHierarchy` (SuperAdmin, Superuser) —
 * spiegelt serverseitiges `@Authorized([Roles.Admin, Roles.Superuser])`.
 */
export const isAdminOrHigher = (role: Roles): boolean => {
    const index = roleHierarchy.indexOf(role);

    return index !== -1 && index <= roleHierarchy.indexOf(Roles.Admin);
};
